/**
 * Calculate elapsed business minutes between two Date objects.
 * Business hours = serviceStart to serviceEnd, Mon–Fri only.
 * @param {Date} startDt  - when the clock started (fault_reported_at)
 * @param {Date} endDt    - "now" (or resolved_at for closed tickets)
 * @param {string} serviceStart - "HH:MM" e.g. "09:00"
 * @param {string} serviceEnd   - "HH:MM" e.g. "17:00"
 * @returns {number} elapsed minutes (integer)
 */
export function businessMinutes(startDt, endDt, serviceStart = '09:00', serviceEnd = '17:00') {
  const [sh, sm] = serviceStart.split(':').map(Number);
  const [eh, em] = serviceEnd.split(':').map(Number);

  let total = 0;
  let cur = new Date(startDt);
  const end = new Date(endDt);

  if (cur >= end) return 0;

  // Snap cur to the start of the business window if before it on the same day
  const snapToWindowStart = (d) => {
    const ws = new Date(d);
    ws.setHours(sh, sm, 0, 0);
    return ws;
  };

  const windowEnd = (d) => {
    const we = new Date(d);
    we.setHours(eh, em, 0, 0);
    return we;
  };

  const isWeekend = (d) => { const dow = d.getDay(); return dow === 0 || dow === 6; };

  const nextMonday = (d) => {
    const nd = new Date(d);
    nd.setDate(nd.getDate() + (8 - nd.getDay()) % 7 || 7);
    nd.setHours(sh, sm, 0, 0);
    return nd;
  };

  // Clamp start: if before window, snap to window start; if after window, move to next biz day
  if (!isWeekend(cur)) {
    const ws = snapToWindowStart(cur);
    const we = windowEnd(cur);
    if (cur < ws) cur = ws;
    else if (cur >= we) {
      cur.setDate(cur.getDate() + 1);
      cur.setHours(sh, sm, 0, 0);
    }
  }

  let safety = 0;
  while (cur < end && safety++ < 5000) {
    if (isWeekend(cur)) {
      cur = nextMonday(cur);
      continue;
    }

    const we = windowEnd(cur);
    if (cur >= we) {
      cur.setDate(cur.getDate() + 1);
      cur.setHours(sh, sm, 0, 0);
      continue;
    }

    const periodEnd = end < we ? end : we;
    total += (periodEnd - cur) / 60000;

    cur = new Date(we);
    cur.setDate(cur.getDate() + 1);
    cur.setHours(sh, sm, 0, 0);
  }

  return Math.round(total);
}

/**
 * Format minutes into a readable string: "3d 2h 15m" or "45m" etc.
 */
export function formatBusinessTime(totalMins) {
  if (totalMins <= 0) return '0m';
  const d = Math.floor(totalMins / 480);   // 8h work day
  const remaining = totalMins % 480;
  const h = Math.floor(remaining / 60);
  const m = remaining % 60;
  const parts = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0) parts.push(`${h}h`);
  const showMins = h > 0 ? m > 0 : true;
  if (showMins) parts.push(`${Math.round(m)}m`);
  return parts.join(' ');
}

/**
 * Is the clock currently running? (within business hours, weekday)
 */
export function isBusinessHoursNow(serviceStart = '09:00', serviceEnd = '17:00') {
  const now = new Date();
  const dow = now.getDay();
  if (dow === 0 || dow === 6) return false;
  const [sh, sm] = serviceStart.split(':').map(Number);
  const [eh, em] = serviceEnd.split(':').map(Number);
  const mins = now.getHours() * 60 + now.getMinutes();
  return mins >= sh * 60 + sm && mins < eh * 60 + em;
}
